<template>
	<v-card v-if="node" class="container px-2" flat>
		<v-list class="list">
			<!-- PAIRING -->
			<v-list-group class="list-group" :value="true">
				<template #activator>
					<v-list-item-content>
						<v-list-item-title class="section-title">Actions</v-list-item-title>
					</v-list-item-content>
				</template>
				<v-divider />

				<v-list-item class="list-item" dense link @click="pairingDevice = node">
					<v-list-item-title>PAIR</v-list-item-title>
				</v-list-item>
				<v-list-item
					v-if="showTestsAction"
					class="list-item"
					dense
					link
					@click="testingDevice = node"
				>
					<v-list-item-title>RUN TESTS</v-list-item-title>
				</v-list-item>
			</v-list-group>

			<!-- COMMANDS -->
			<prop-list-group
				#default="{ name }"
				:node="node"
				subpath="commands"
				@click:list-item="execute"
			>
				<v-list-item-title>{{ upperCase(name) }}</v-list-item-title>
			</prop-list-group>

			<!-- PROPERTIES -->
			<prop-list-group
				#default="{ subpath }"
				:node="node"
				subpath="properties"
				@click:list-item="setEditProp"
			>
				<prop-list-row
					:editing="isEditing(node, subpath)"
					:node="node"
					:subpath="subpath"
					@edit:done="setEditProp"
				/>
			</prop-list-group>

			<!-- VARIABLES -->
			<prop-list-group
				#default="{ subpath }"
				:node="node"
				subpath="variables"
				@click:list-item="setEditProp"
			>
				<prop-list-row
					:editing="isEditing(node, subpath)"
					:node="node"
					:subpath="subpath"
					@edit:done="setEditProp"
				/>
			</prop-list-group>

			<!-- EVENTS -->
			<prop-list-group
				#default="{ subpath }"
				:node="node"
				subpath="events"
				@click:list-item="fire"
			>
				<event-list-row :node="node" :subpath="subpath" />
			</prop-list-group>
		</v-list>

		<!-- Dialogs -->
		<PairingWizard :device="pairingDevice" @close="pairingDevice = null" />
		<CommandArgsDialog ref="cmdArgsDialog" />
		<CommandResultsDialog ref="cmdResultsDialog" />
		<TestDialog :device="testingDevice" @close="testingDevice = null" />

		<!-- Snackbars -->
		<nav-snackbar
			v-model="showCmdSuccessSnack"
			color="success"
			text-color="white"
			:timeout="2500"
			top
			>Command executed successfully</nav-snackbar
		>

		<!-- Loader overlay -->
		<v-overlay :value="showLoader">
			<v-progress-circular indeterminate size="64" />
		</v-overlay>
	</v-card>
</template>

<style lang="scss" scoped>
// Fix messed up Vuetify CSS that makes drop-down list chevrons appear much
// larger than they should.
::v-deep .v-list-group__header__append-icon i.v-icon {
	font-size: 0.875rem;
}
</style>

<style lang="scss" scoped>
.container {
	padding: 0 24px 20px;
	overflow: hidden;
}

.container,
.container > .list {
	background: transparent;
}
</style>

<script>
import _ from "lodash";
import { mapGetters, mapActions } from "vuex";

import PropListGroup from "./PropListGroup";
import PropListRow from "./PropListRow";
import EventListRow from "./EventListRow";
import CommandArgsDialog from "./CommandArgsDialog";
import CommandResultsDialog from "./CommandResultsDialog";
import TestDialog from "./TestDialog";

import PairingWizard from "@/views/install/nested-flow/pairing/PairingWizard";

export default {
	components: {
		PropListGroup,
		PropListRow,
		EventListRow,
		CommandArgsDialog,
		CommandResultsDialog,
		TestDialog,
		PairingWizard,
	},

	props: ["nodeGuid"],

	data: () => ({
		showTestsAction: false,
		showLoader: false,
		showCmdSuccessSnack: false,

		propEditNode: null,
		propEditSubpath: null,

		pairingDevice: null,
		testingDevice: null,
	}),

	computed: {
		...mapGetters({
			nodeByGuid: "controller/nodes/nodeByGuid",
			liveNodeByGuid: "controller/nodes/liveNodeByGuid",
			liveNodeByPath: "controller/nodes/liveNodeByPath",
		}),

		node() {
			return this.nodeGuid ? this.nodeByGuid(this.nodeGuid) : null;
		},

		events() {
			const eventNames = _.chain(this.node.events || {})
				.keys()
				.sort()
				.value();
			const events = _.reduce(
				eventNames,
				(accum, name) => {
					const key = _.startCase(name);
					accum[key] = `events/${name}`;
					return accum;
				},
				{}
			);

			return events;
		},

		counts() {
			return {
				commands: _.size(this.node.commands || {}),
				properties: _.size(this.node.properties || {}),
				variables: _.size(this.node.variables || {}),
				events: _.size(this.events || {}),
			};
		},
	},

	watch: {
		// Detect change in selected node and update whether or not we want the
		// user to be able to run device tests for that node
		async node(is, was) {
			this.showTestsAction = false;
			if (!is) return;

			const tests = await this.loadDeviceTests(is);
			this.showTestsAction = !_.isEmpty(tests);
		},
	},

	methods: {
		...mapActions({
			loadDeviceTests: "install/loadDeviceTests",
			executeCommand: "controller/nodes/executeCommand",
		}),

		upperCase: _.upperCase,

		isEditing(node, subpath) {
			const nodeGuid = _.get(node, "meta.guid");
			const editNodeGuid = _.get(this.propEditNode, "meta.guid");
			return nodeGuid === editNodeGuid && subpath === this.propEditSubpath;
		},

		async execute({ node, subpath }) {
			const commandName = _.startCase(_.last(subpath.split("/")));

			let gatheredArgs = null;
			gatheredArgs = await this.$refs.cmdArgsDialog.pop({
				node,
				subpath,
			});
			if (gatheredArgs === false) return; // User hit cancel in dialog

			let resp = null;
			let success = true;
			try {
				this.showLoader = true;

				// Execute command on live node
				resp = await this.executeCommand({
					node: this.node,
					subpath,
					commandParams: gatheredArgs,
				});

				if (resp && _.isBoolean(resp)) {
					// Notify user of success without response
					this.showCmdSuccessSnack = true;
					return;
				}
			} catch (e) {
				success = false;
				resp = e.message;
			} finally {
				this.showLoader = false;
			}

			// Show user success or failure message
			this.$refs.cmdResultsDialog.pop({
				success,
				response: resp,
				command: commandName,
			});
		},

		setEditProp(target) {
			let node = null;
			let subpath = null;
			if (target) {
				node = target.node;
				subpath = target.subpath;
			}
			this.propEditNode = node;
			this.propEditSubpath = subpath;
		},

		fire({ node, subpath }) {
			const eventNode = this.liveNodeByPath(`${node.path}/${subpath}`);
			return eventNode.fire();
		},
	},
};
</script>
